import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { TbAlertCircleFilled, TbMapPinFilled } from 'react-icons/tb';
import OutsideClickHandler from 'react-outside-click-handler';

import { useFetchAutocompleteCityMutation } from '@/api/hooks/useCity';
import { CityResponse } from '@/api/lib/city';

interface AutocompleteCityProps {
  onCitySelect: (city: CityResponse) => void;
  defaultCity?: string | null;
  onPartnerCityFound?: ({ partnerName, partnerId }: { partnerName?: string; partnerId?: string }) => void;
  isOnError?: boolean;
}

const AutocompleteCity: React.FC<AutocompleteCityProps> = ({ onCitySelect, defaultCity, onPartnerCityFound, isOnError = false }) => {
  const [search, setSearch] = useState(defaultCity || '');
  const [cities, setCities] = useState<CityResponse[]>([]);

  const [showDropdown, setShowDropdown] = useState(false);
  const [userHasTyped, setUserHasTyped] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { mutate: fetchCities, isLoading } = useFetchAutocompleteCityMutation({
    onSuccess: (data) => {
      setCities(data);

      if (isFirstLoad && data.length > 0) {
        setSearch(data[0].displayName + ' (' + data[0].id + ')');
        onCitySelect(data[0]);
        setIsFirstLoad(false);
      }
    }
  });

  useEffect(() => {
    if (defaultCity) {
      fetchCities({ search: defaultCity, withPartner: true });
    }
  }, [defaultCity, fetchCities]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    setUserHasTyped(true);

    if (value.length > 2) {
      fetchCities({ search: value, withPartner: true });
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSelectCity = (city: CityResponse) => {
    setSearch(city.displayName + ' (' + city.id + ')');
    setShowDropdown(false);
    onCitySelect(city);

    if (onPartnerCityFound) {
      onPartnerCityFound({ partnerName: city.partner, partnerId: city.partnerId });
    }
  };

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setShowDropdown(false);
      }}
    >
      <div className="relative z-10 ">
        <div className="flex items-center h-full group">
          <TbMapPinFilled
            className={clsx(
              isOnError ? 'border-red-400' : 'border-gray-300',
              'text-4xl  border-l border-y h-[42px] md:h-[38px] p-1 rounded-l-lg shadow-sm group-focus-within:border-primary'
            )}
          />
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Rechercher une ville"
            className={clsx(
              isOnError ? 'border-red-400 text-red-600' : 'border-gray-300 text-black',
              'w-full px-4 py-2 rounded-r-lg focus:border-primary focus:outline-none focus:ring-0'
            )}
            onFocus={() => userHasTyped && setShowDropdown(true)}
          />
        </div>
        {showDropdown && (
          <ul className="absolute  w-full mt-1 top-10 bg-white border border-gray-300 overflow-auto rounded-lg shadow-lg max-h-64">
            {isLoading ? (
              <li className="px-4 py-2 text-gray-500">Chargement...</li>
            ) : cities.length > 0 ? (
              cities.map((city) => (
                <li
                  key={city.id}
                  onClick={() => handleSelectCity(city)}
                  className={clsx(
                    'px-4 py-2 flex flex-col gap-1 cursor-pointer duration-100 ease-in-out hover:bg-shades-primary-hover/80 hover:text-white',
                    city !== cities[cities.length - 1] && 'border-b border-gray-200'
                  )}
                >
                  <span className="flex gap-1 items-center">
                    <TbMapPinFilled className={clsx(city.partner ? 'text-gray-600' : 'text-primary', 'text-2xl')} />
                    <strong>{city.displayName}</strong>
                  </span>
                  {city.partner && (
                    <div className="flex gap-1 items-center justify-center">
                      <span className="text-sm">-------</span>
                      <TbAlertCircleFilled className="text-red-600 text-xl" />
                      <span className="text-sm">
                        Appartient à <strong>{city.partner}</strong>
                      </span>
                      <span className="text-sm">-------</span>
                    </div>
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">Tapez le nom de la ville</li>
            )}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default AutocompleteCity;
